import React from 'react'
import { useRouter } from 'next/router'
import useLazyCategoryRender from 'src/hooks/useLazyCategoryRender'
import EventEmitter from '@jarvis.oddle/core/tools/eventEmitter'
import {
  PortalDest,
  PortalProvider
} from '@jarvis.oddle/core/components/Portal'
import styled from 'styled-components'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { padding, paddingSizes } from '@jarvis.oddle/core/theme/default'
import Category from './Category'
import { Grid, Row } from '@jarvis.oddle/core/components/Grid'
import CategoriesNavigation from 'src/components/molecules/CategoriesNavigation'
import ProductImageState from 'src/components/molecules/ProductImageState'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectPreviewMode } from 'src/redux/customisation/selector'
import { selectMenuProductList } from 'src/redux/menu'
import { selectIsBot } from 'src/redux/meta'
import ResponsiveSection from 'src/components/core/ResponsiveSection'

const PRODUCTS_HORIZONTAL_NAV_CHANNEL = 'PRODUCTS_HORIZONTAL_NAV_CHANNEL'

const Wrapper = styled.div`
  padding: 0;
  ${breakpoint('desktop')`
    padding: ${padding[paddingSizes.medium]} 0;
  `}
`

const StyledResponsiveSection = styled(ResponsiveSection)`
  margin-top: 0;
  margin-bottom: 0;
  padding: 0;
`

const ProductSection = React.memo(() => {
  const router = useRouter()
  const previewMode = useAppSelector(selectPreviewMode)
  const { data: menu } = useAppSelector(selectMenuProductList)
  const isBot = useAppSelector(selectIsBot)
  const {
    shouldShowCategoryDetails,
    shouldShowCategorySkeleton,
    showCategorySkeleton,
    showCategoryDetails
  } = useLazyCategoryRender({ menu, disabled: isBot })
  const categoryVisibilityEmitter = React.useMemo(() => EventEmitter(), [])

  const hideCategoriesNavigation = menu?.length === 1

  const getCategory = React.useCallback(
    (id) => menu?.find((cat) => id?.includes(cat.id)),
    [menu]
  )
  const scrollToCategory = React.useCallback((catId) => {
    // NOTE: We need to override default behaviour to manually scroll to the category anchor
    // Timeout is set to 100ms as there's a browser issue in Firefox which causes automated regression tests unable to display the category intermittently
    setTimeout(() => {
      const categoryAnchor = document.getElementById(catId)
      categoryAnchor && categoryAnchor.scrollIntoView(true)
    }, 100)
  }, [])

  const onCategoryNavItemClick = React.useCallback(
    (e, catId) => {
      e.preventDefault()
      showCategorySkeleton(catId)
      scrollToCategory(catId)
    },
    [scrollToCategory, showCategorySkeleton]
  )

  // NOTE: Manually scroll to category when page is soft-routed to a category
  React.useEffect(() => {
    const onHashChangeStart = (url) => {
      const catId = url.split('#')[1]
      const category = getCategory(catId)
      if (category?.id) {
        showCategorySkeleton(category.id)
        scrollToCategory(category.id)
      }
    }

    router.events.on('hashChangeStart', onHashChangeStart)
    return () => {
      router.events.off('hashChangeStart', onHashChangeStart)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // NOTE: Manually scroll to category on initial page load if url hash is a category id
  React.useEffect(() => {
    const catId = (window.location.hash || '').split('#')[1]
    const category = getCategory(catId)
    if (category?.id) {
      showCategorySkeleton(category.id)
      scrollToCategory(category.id)
    }
  }, [getCategory, scrollToCategory, showCategorySkeleton])

  return (
    <PortalProvider>
      <Wrapper>
        <PortalDest channel={PRODUCTS_HORIZONTAL_NAV_CHANNEL} />
        <StyledResponsiveSection>
          <ResponsiveSection.Container>
            <Grid>
              <Row>
                <ProductImageState>
                  {({
                    showImage,
                    showDefaultImage,
                    productImageAspect,
                    listView,
                    isTopNavLayout
                  }) => {
                    const showSideNav =
                      !isTopNavLayout && !hideCategoriesNavigation
                    const showHorizontalNav =
                      isTopNavLayout && !hideCategoriesNavigation
                    const productSectionSize = showSideNav ? 9 : 12
                    return (
                      <CategoriesNavigation
                        syncScroll
                        hideCategoriesNavigation={hideCategoriesNavigation}
                        showHorizontalNav={showHorizontalNav}
                        categories={menu}
                        categoryVisibilityEmitter={categoryVisibilityEmitter}
                        productSectionSize={productSectionSize}
                        horizontalNavChannel={PRODUCTS_HORIZONTAL_NAV_CHANNEL}
                        onClickItem={onCategoryNavItemClick}
                      >
                        {menu?.map((category) => (
                          <Category
                            key={category.id}
                            categoryVisibilityEmitter={
                              categoryVisibilityEmitter
                            }
                            category={category}
                            isCategoryLoaded={shouldShowCategoryDetails(
                              category.id
                            )}
                            isCategoryDisplayed={shouldShowCategorySkeleton(
                              category.id
                            )}
                            showHorizontalNav={showHorizontalNav}
                            previewMode={previewMode}
                            showImage={showImage}
                            showDefaultImage={showDefaultImage}
                            productImageAspect={productImageAspect}
                            listView={listView}
                            showSideNav={showSideNav}
                            showCategoryDetails={showCategoryDetails}
                          />
                        ))}
                      </CategoriesNavigation>
                    )
                  }}
                </ProductImageState>
              </Row>
            </Grid>
          </ResponsiveSection.Container>
        </StyledResponsiveSection>
      </Wrapper>
    </PortalProvider>
  )
})

export default ProductSection
